<template>
  <UIInputAutocomplete
    ref="autocompleteRef"
    v-model="dropdownValue"
    :data
    fetchable
    @fetch:data="handleSearch"
    @click:item="handleSelect"
    @ready="handleReady"
  >
    <template #trailing>
      <component :is="loading ? UIPreloader : MagnifyingGlassIcon" />
    </template>
    <template #item="{ item, itemClasses, onClick }">
      <div
        class="ui-dropdown-list-item eod-search__row"
        :class="[rowClasses, itemClasses]"
        @click="onClick"
      >
        <div
          v-tooltip="item.label"
          class="eod-search__cell eod-search__cell--name"
        >
          {{ item.label }}
        </div>
        <div class="eod-search__cell">{{ item.meta?.code }}</div>
        <template v-if="!short">
          <div v-tooltip="item.meta?.type" class="eod-search__cell">
            {{ item.meta?.type }}
          </div>
          <div class="eod-search__cell">{{ item.meta?.exchange }}</div>
        </template>
      </div>
    </template>
  </UIInputAutocomplete>
</template>

<script setup lang="ts">
import { computed, inject, ref, useTemplateRef, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'

import { AssetSecurityMaster } from '@types'
import { WSInstance, WSResponse } from '@/services/ws/utils/types'

import { THROTTLE_DELAY } from './utils/const'

import { useUserStore } from '@/store/user'

import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import { UIInputAutocomplete } from '@ui/fields'
import { UIPreloader } from '@ui/core'

type Props = {
  disabled?: boolean
  short?: boolean
}

type Emits = {
  (e: 'ready', el: HTMLInputElement): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const modelValue = defineModel<AssetSecurityMaster>()

defineExpose({
  async search(data: string) {
    dropdownValue.value = data
    autocompleteRef.value?.setSearchValue(data)
  },
  async cancel(data?: string) {
    dropdownValue.value = data
    if (data) return
  },
  focus() {
    inputRef.value?.focus()
  },
})

const userStore = useUserStore()

const ws = inject<WSInstance>('ws')

const autocompleteRef = useTemplateRef('autocompleteRef')
const inputRef = ref<HTMLInputElement>()

const dropdownValue = ref<string>()
const loading = ref(false)
const uniqueId = ref()

const assetsList = ref<AssetSecurityMaster[]>([])

const data = computed(() =>
  assetsList.value.map(item => ({
    value: item.name,
    label: item.name,
    meta: {
      code: item.code,
      type: item.type,
      exchange: item.exchange,
    },
  })),
)

const rowClasses = computed(() => ({ 'eod-search__row--short': props.short }))

const handleSearch = (data: string) => {
  uniqueId.value = data
  if (data === '') {
    assetsList.value = []
    loading.value = false
    return
  }
  loading.value = true
  fetch(data)
}

const fetch = useDebounceFn(async (data?: string) => {
  if (!data) {
    loading.value = false
    assetsList.value = []
    return
  }
  ws?.send('security_master', 'eod_search', data, { symbol: data })
}, THROTTLE_DELAY)

const handleSelect = (value: string) => {
  const data = assetsList.value.find(item => item.name === value)
  modelValue.value = data
}

const handleReady = (el: HTMLInputElement) => {
  inputRef.value = el
  emit('ready', el)
}

watch(
  () => userStore.isAuthenticated,
  value => {
    if (!value) return
    ws?.subscribe(
      'security_master',
      'eod_search',
      async (message: WSResponse<AssetSecurityMaster[]>, requestId: string) => {
        if (requestId !== uniqueId.value) return
        loading.value = false
        assetsList.value = message.data || []
      },
    )
  },
  { immediate: true },
)
</script>

<style>
.eod-search {
  &__row {
    @apply flex-auto;
    @apply grid gap-1;
    @apply grid-cols-[minmax(5rem,1fr)_minmax(2rem,5rem)_minmax(2rem,8rem)_minmax(2rem,3rem)];

    &--short {
      @apply grid-cols-[minmax(6rem,1fr)_minmax(2rem,1fr)];
    }
  }
  &__cell {
    @apply truncate;

    &--name {
      @apply font-medium;
    }
  }
}
</style>
